/**
 * Context del módulo WhatsApp — vive en AppLayout, activo en cualquier ruta
 * `/chats/*`. Single source of truth del workspace (patrón Chatwoot/Linear).
 *
 * Centraliza catálogo (phone lines + tags), selección del sidebar, lista de
 * conversations + realtime, mentions y counts derivados — para que los badges
 * del sidebar funcionen en cualquier sub-ruta sin recargar.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { vaEnLaBandeja } from '@/lib/whatsapp/estadosDeConversacion';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useDatosCacheados } from '@/hooks/useDatosCacheados';
import { useAuth } from '@smarty/shared';
import { useWaPhoneLines } from '@/hooks/useWaPhoneLines';
import { useWaPermissions } from '@/hooks/useWaPermissions';
import { useWaConversations, type WaConversation } from '@/hooks/useWaConversations';
import { useWaInboxCounts } from '@/hooks/useWaInboxCounts';
import { useWaUnreadMentions } from '@/hooks/useWaUnreadMentions';
import { useWaInboxRealtime } from '@/hooks/useWaInboxRealtime';
import { parseLineParam } from '@/lib/conversationSlug';
import { logError } from '@/lib/logger';

export type SidebarSelection =
  | { kind: 'bucket'; value: 'all' | 'mentions' | 'unattended' }
  | { kind: 'channel'; value: string }
  /**
   * Una ENTRADA que no es de WhatsApp (chat del sitio, Mercado Libre, correo).
   *
   * 🪤 ES UNA CLASE APARTE DE `channel`, Y TIENE QUE SERLO. `channel` lleva un
   * `wa_phone_lines.id` y filtra por `phone_line_id`; estas conversaciones NO
   * tienen línea de WhatsApp — se filtran por `inbox_id`. Meterlas en la misma
   * clase haría que el filtro no devuelva nada y pareciera que no hay
   * conversaciones, en vez de fallar.
   */
  | { kind: 'entrada'; value: string }
  | { kind: 'tag'; value: string };

export type WaStatusFilter = 'active' | 'pending' | 'snoozed' | 'resolved' | 'archived' | 'all';

export interface PhoneLine {
  id: string;
  display_id: number;
  label: string | null;
  phone_number: string | null;
}

/** Una entrada de la bandeja que no es WhatsApp. */
export interface EntradaDeCanal {
  id: string;
  name: string;
  channel_type: 'web' | 'api' | 'email';
}

export interface TagMeta {
  name: string;
  color: string;
  sort_order: number;
  /** Su lugar en la lista del celular (vacío si la etiqueta es sólo de Smarty): la lista de chats ordena con él. */
  wa_label_orden: number | null;
}

/** Umbral default para considerar una conversación "desatendida" (sin respuesta del agente). */
const UNATTENDED_THRESHOLD_MS = 30 * 60 * 1000;

interface Ctx {
  // ─── Catálogo ───
  phoneLines: PhoneLine[];
  /**
   * Entradas de canales que no son WhatsApp.
   *
   * Sin esto el sidebar solo listaba líneas de WhatsApp y las conversaciones de
   * otros canales quedaban invisibles: existen en «Todas», pero enterradas entre
   * cientos y sin ninguna forma de llegar a ellas.
   */
  entradasDeCanal: EntradaDeCanal[];
  tagCatalog: TagMeta[];

  // ─── Selección sidebar (bucket/channel/tag) ───
  selection: SidebarSelection;
  setSelection: (sel: SidebarSelection) => void;

  // ─── Conversations ───
  conversations: WaConversation[];
  setConversations: React.Dispatch<React.SetStateAction<WaConversation[]>>;
  conversationsLoading: boolean;
  /** Refetch manual (lo usan los handlers de acciones tras cambios destructivos). */
  loadConversations: () => Promise<void>;
  /** Paginación por cursor: hay más allá de lo cargado. */
  hasMoreConversations: boolean;
  /** Cargando la siguiente tanda (scroll infinito). */
  loadingMoreConversations: boolean;
  /** Cargar la siguiente tanda (lo dispara el scroll de la lista al llegar al final). */
  loadMoreConversations: () => Promise<void>;
  /** Subconjunto de `conversations` con status === 'archived'. Derivado
   *  con useMemo — single source of truth: las mutations sobre
   *  `conversations` se reflejan acá automáticamente. Alimenta el overlay
   *  drawer de Archivados (paridad WhatsApp Web). */
  archivedConversations: WaConversation[];
  /** Subconjunto de `conversations` con status !== 'archived'. La lista
   *  principal (sidebar, scoped, counts) usa esto — los archivados solo
   *  aparecen explícitamente en el overlay. */
  nonArchivedConversations: WaConversation[];
  /** Ref siempre fresca — para realtime listeners externos que necesiten leer
   *  la lista actual sin causar re-renders. */
  conversationsRef: React.MutableRefObject<WaConversation[]>;

  // ─── Status filter (drives qué conversaciones se cargan) ───
  statusFilter: WaStatusFilter;
  setStatusFilter: (s: WaStatusFilter) => void;

  // ─── Counts derivados (memoizados sobre conversations + mentions) ───
  bucketCounts: { all: number; mentions: number; unattended: number };
  channelCounts: Record<string, number>;
  tagCountsMap: Record<string, number>;

  // ─── Mentions ───
  mentionConversationIds: Set<string>;
  markMentionAsRead: (conversationId: string) => Promise<void>;

  // ─── Presencia (en línea / escribiendo), por teléfono ───
  /** Vive acá y no en la bandeja porque el canal que la alimenta es del tenant, no de la pantalla. */
  presenceMap: Map<string, 'composing' | 'recording' | 'available'>;

  /**
   * La bandeja registra acá qué hacer cuando una conversación desaparece (la borran, o un cambio
   * de estado la saca del filtro) y cuando entra un mensaje en un chat que no está en pantalla.
   * Son decisiones de esa pantalla —deseleccionar, avisar—, no del canal de realtime.
   */
  registrarAccionesDeBandeja: (acciones: {
    onConversacionFuera?: (convId: string) => void;
    showToast?: (c: import('@/hooks/useInboxToast').ToastContent) => void;
  }) => () => void;
}

const DEFAULT_SELECTION: SidebarSelection = { kind: 'bucket', value: 'all' };

const WaInboxNavContext = createContext<Ctx | null>(null);

/**
 * Deriva la selección del sidebar a partir de los query params de la URL.
 *
 * Para `channel=L-1` necesitamos resolver el slug → UUID usando el catálogo
 * de líneas (el filtrado interno opera con el UUID). Si todavía no cargó el
 * catálogo devolvemos `null` — el effect consumidor lo reintenta cuando
 * `phoneLines` cambia.
 */
function selectionFromSearch(
  search: string,
  phoneLines: PhoneLine[],
): SidebarSelection | null {
  const params = new URLSearchParams(search);
  const bucket = params.get('bucket');
  if (bucket === 'all' || bucket === 'mentions' || bucket === 'unattended') {
    return { kind: 'bucket', value: bucket };
  }
  const channelRaw = params.get('channel');
  if (channelRaw) {
    const parsed = parseLineParam(channelRaw);
    if (parsed.uuid) return { kind: 'channel', value: parsed.uuid };
    if (parsed.displayId != null) {
      const match = phoneLines.find((pl) => pl.display_id === parsed.displayId);
      if (!match) return null; // catálogo aún no listo o línea inexistente
      return { kind: 'channel', value: match.id };
    }
  }
  const tag = params.get('tag');
  if (tag) return { kind: 'tag', value: tag };
  return DEFAULT_SELECTION;
}

export function WaInboxNavProvider({ children }: { children: ReactNode }) {
  const { tenantId, user } = useAuth();
  const waPerms = useWaPermissions();
  const location = useLocation();

  const [tagCatalog, setTagCatalog] = useState<TagMeta[]>([]);
  const [selection, setSelection] = useState<SidebarSelection>(DEFAULT_SELECTION);
  const [statusFilter, setStatusFilter] = useState<WaStatusFilter>('active');

  // Líneas del tenant — hook compartido con realtime. Stripeamos a la forma
  // mínima que necesita el sidebar (sin display_id raw, etc.).
  const { lines: rawLines } = useWaPhoneLines(tenantId, { onlyActive: true });
  const phoneLines = useMemo<PhoneLine[]>(
    () => rawLines.map(({ id, display_id, label, phone_number }) => ({
      id, display_id, label, phone_number,
    })),
    [rawLines],
  );

  // Entradas que no son WhatsApp. Se piden aparte de `wa_phone_lines` porque son
  // otra tabla: `inboxes` es la polimórfica (ADR 0022) y las líneas son solo su
  // caso WhatsApp.
  const { datos: entradasCrudas } = useDatosCacheados<EntradaDeCanal[]>({
    clave: ['entradas-no-whatsapp', tenantId],
    activo: !!tenantId,
    frescura: 300_000,
    traer: async () => {
      const { data, error } = await supabase
        .from('inboxes')
        .select('id, name, channel_type')
        .eq('tenant_id', tenantId!)
        .neq('channel_type', 'whatsapp')
        .eq('setup_status', 'active')
        .order('name');
      if (error) throw error;
      return (data ?? []) as unknown as EntradaDeCanal[];
    },
  });
  const entradasDeCanal = useMemo(() => entradasCrudas ?? [], [entradasCrudas]);

  // ─── Conversations + mentions ───
  // La carga + filtros viven aquí ahora, no en WhatsAppInboxPage. Esto permite
  // que los counts del sidebar estén vivos en cualquier ruta de /chats/*.
  const {
    conversations,
    setConversations,
    loading: conversationsLoading,
    loadConversations,
    hasMore: hasMoreConversations,
    loadingMore: loadingMoreConversations,
    loadMore: loadMoreConversations,
  } = useWaConversations({
    tenantId,
    statusFilter,
    isAgent: waPerms.isAgent,
    userId: user?.id,
    // 🪤 EL FILTRO DE CANAL TIENE QUE SER DEL SERVIDOR, NO DEL CLIENTE. La lista
    // se pagina por fecha: filtrando solo en memoria, las conversaciones viejas
    // de un canal poco activo NUNCA se cargan y el canal se ve vacío aunque
    // tenga historial. Es lo que pasaba con las 10 del chat del sitio, de abril,
    // sepultadas bajo cientos de conversaciones de WhatsApp.
    phoneLineId: selection.kind === 'channel' ? selection.value : null,
    inboxId: selection.kind === 'entrada' ? selection.value : null,
  });

  // Contadores REALES del servidor (sobre todas las filas, no sobre las ≤100
  // cargadas). Se recuenta cuando cambia la cantidad de conversaciones cargadas
  // — proxy barato de "entró/salió/cambió algo". Best-effort: si el RPC falla,
  // los counts derivados de abajo siguen siendo el fallback.
  const realCounts = useWaInboxCounts({
    tenantId,
    agentId: waPerms.isAgent ? user?.id : null,
    bump: conversations.length,
  });

  // archivedConversations es DERIVED de conversations — archived es solo
  // una etiqueta (status), no una entidad separada. Single source of truth:
  // toda mutación (mark-as-read, pin, mute, archive, etc.) sobre
  // `conversations` se refleja automáticamente en archived sin sync manual.
  const archivedConversations = useMemo(
    () => conversations.filter((c) => c.status === 'archived'),
    [conversations],
  );

  // Lista normal sin archivados — alimenta TODA la UI principal (sidebar
  // counts, scopedConversations, filtros por canal/tag). Los archivados
  // solo aparecen explícitamente en el overlay drawer.
  const nonArchivedConversations = useMemo(
    // Lista blanca compartida: un estado interno nuevo (como `merged`, el cascarón que deja fusionar
    // un chat partido) no se cuela solo en la pantalla de nadie.
    () => conversations.filter((c) => vaEnLaBandeja(c.status)),
    [conversations],
  );

  // Ref siempre fresca para que los realtime listeners externos no necesiten
  // volverse a suscribir cuando `conversations` cambie.
  const conversationsRef = useRef<WaConversation[]>(conversations);
  useEffect(() => { conversationsRef.current = conversations; }, [conversations]);

  /*
   * ─── Realtime del canal, montado ACÁ y no en la bandeja ───
   *
   * 🪤 ESTABA EN WhatsAppInboxPage, Y ESE ERA EL BUG. Fuera de la ruta de Chats nadie escuchaba
   * nada: la lista del encabezado se quedaba con el mensaje viejo, y un mensaje enviado desde una
   * ventana acoplada mostraba un solo check para siempre aunque el cliente ya lo hubiera leído. No
   * era un problema de los checks: era que el oído estaba dentro de una sola pantalla.
   *
   * Acá arriba se suscribe una vez por tenant y sirve a todo el admin. Los mensajes se reparten
   * por el registro de hilos abiertos (waThreadRegistry), así que da igual si el chat está en la
   * bandeja, en una ventana acoplada, o en las dos a la vez.
   */
  const [presenceMap, setPresenceMap] = useState<Map<string, 'composing' | 'recording' | 'available'>>(new Map());
  const statusFilterRef = useRef<string>(statusFilter);
  useEffect(() => { statusFilterRef.current = statusFilter; }, [statusFilter]);
  const loadConversationsRef = useRef<(() => Promise<void>) | null>(loadConversations);
  useEffect(() => { loadConversationsRef.current = loadConversations; }, [loadConversations]);

  // Acciones que solo tienen sentido en la bandeja (deseleccionar, avisar). Van por ref para que
  // registrarlas no obligue a re-suscribir el canal.
  const accionesDeBandeja = useRef<{
    onConversacionFuera?: (convId: string) => void;
    showToast?: (c: import('@/hooks/useInboxToast').ToastContent) => void;
  }>({});
  const registrarAccionesDeBandeja = useCallback((acciones: {
    onConversacionFuera?: (convId: string) => void;
    showToast?: (c: import('@/hooks/useInboxToast').ToastContent) => void;
  }) => {
    accionesDeBandeja.current = acciones;
    return () => { accionesDeBandeja.current = {}; };
  }, []);

  useWaInboxRealtime({
    tenantId,
    statusFilterRef,
    loadConversationsRef,
    conversationsRef,
    setConversations,
    setPresenceMap,
    onConversacionFuera: (id) => accionesDeBandeja.current.onConversacionFuera?.(id),
    showToast: (c) => accionesDeBandeja.current.showToast?.(c),
    // Sólo para medir: `admin_rum_events` exige tenant y usuario, y sin los dos descarta la fila.
    userId: user?.id ?? null,
  });

  // Mentions del usuario actual — alimenta bucketCounts.mentions + filtro
  // bucket='mentions'. El hook se encarga del realtime sobre wa_message_mentions.
  const {
    count: mentionsCount,
    conversationIds: mentionConversationIds,
    markAsRead: markMentionAsRead,
  } = useWaUnreadMentions(tenantId, user?.id);

  // URL → selection: re-sincroniza al cambiar query string o cuando carga
  // el catálogo de líneas (necesario para resolver slugs tipo `L-1`).
  useEffect(() => {
    const next = selectionFromSearch(location.search, phoneLines);
    if (next === null) return; // slug aún no resuelto, catálogo cargando
    setSelection((prev) =>
      prev.kind === next.kind && prev.value === next.value ? prev : next,
    );
  }, [location.search, phoneLines]);

  // Cargar catálogo de etiquetas del tenant (con realtime para reflejar
  // creación/edición/borrado desde otras pantallas sin recargar).
  const loadTags = useCallback(async () => {
    if (!tenantId) return;
    const { data, error } = await supabase
      .from('wa_tags')
      .select('name, color, sort_order, wa_label_orden')
      .eq('tenant_id', tenantId)
      .order('sort_order', { ascending: true })
      .order('name');
    if (error) {
      logError('No se pudo cargar el catálogo de etiquetas WhatsApp', error);
      return;
    }
    setTagCatalog((data ?? []) as TagMeta[]);
  }, [tenantId]);

  useEffect(() => {
    void loadTags();
  }, [loadTags]);

  useEffect(() => {
    if (!tenantId) return;
    const ch = supabase
      .channel(`wa-tags-${tenantId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'wa_tags', filter: `tenant_id=eq.${tenantId}` },
        () => void loadTags(),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(ch);
    };
  }, [tenantId, loadTags]);

  // ─── Counts derivados ───
  // Memoizado para no causar re-renders en cascada del sidebar.
  // IMPORTANTE: los archivados NUNCA cuentan en bucket/channel/tag — son
  // explícitamente ocultos de la vista principal (paridad WhatsApp Web).
  // Solo aparecen en el overlay de "Archivados" con su propio counter.
  const { bucketCounts, channelCounts, tagCountsMap } = useMemo(() => {
    // Preferimos los counts del servidor (todas las filas). Solo caemos al cálculo
    // sobre las ≤100 cargadas si el RPC todavía no respondió o falló — así, ante
    // un error, el peor caso es el comportamiento viejo, no un sidebar en blanco.
    if (realCounts) {
      return {
        bucketCounts: { all: realCounts.total, mentions: mentionsCount, unattended: realCounts.unattended },
        channelCounts: realCounts.byChannel,
        tagCountsMap: realCounts.byTag,
      };
    }
    const now = Date.now();
    const bc = {
      all: nonArchivedConversations.length,
      mentions: mentionsCount,
      unattended: nonArchivedConversations.filter((c) => {
        if (c.last_message_direction !== 'inbound' || !c.last_message_at) return false;
        return now - new Date(c.last_message_at).getTime() > UNATTENDED_THRESHOLD_MS;
      }).length,
    };
    const cc: Record<string, number> = {};
    const tc: Record<string, number> = {};
    for (const c of nonArchivedConversations) {
      if (c.phone_line_id) cc[c.phone_line_id] = (cc[c.phone_line_id] ?? 0) + 1;
      for (const t of c.tags ?? []) tc[t] = (tc[t] ?? 0) + 1;
    }
    return { bucketCounts: bc, channelCounts: cc, tagCountsMap: tc };
  }, [realCounts, nonArchivedConversations, mentionsCount]);

  const value = useMemo<Ctx>(
    () => ({
      phoneLines, entradasDeCanal, tagCatalog,
      selection, setSelection,
      conversations, setConversations, conversationsLoading, loadConversations, conversationsRef,
      hasMoreConversations, loadingMoreConversations, loadMoreConversations,
      archivedConversations,
      nonArchivedConversations,
      statusFilter, setStatusFilter,
      bucketCounts, channelCounts, tagCountsMap,
      mentionConversationIds, markMentionAsRead,
      presenceMap, registrarAccionesDeBandeja,
    }),
    [
      phoneLines, entradasDeCanal, tagCatalog,
      selection, setSelection,
      conversations, setConversations, conversationsLoading, loadConversations,
      hasMoreConversations, loadingMoreConversations, loadMoreConversations,
      archivedConversations, nonArchivedConversations,
      statusFilter, setStatusFilter,
      bucketCounts, channelCounts, tagCountsMap,
      mentionConversationIds, markMentionAsRead,
      presenceMap, registrarAccionesDeBandeja,
    ],
  );

  return <WaInboxNavContext.Provider value={value}>{children}</WaInboxNavContext.Provider>;
}

export function useWaInboxNav(): Ctx {
  const ctx = useContext(WaInboxNavContext);
  if (!ctx) throw new Error('useWaInboxNav debe usarse dentro de WaInboxNavProvider');
  return ctx;
}

/**
 * Igual que `useWaInboxNav` pero devuelve `null` en vez de reventar cuando no hay provider.
 *
 * 🪤 EL ENCABEZADO SE RENDERIZA EN TRES LUGARES Y SOLO UNO TIENE ESTE PROVIDER. `AppLayout` sí;
 * `SettingsLayout` y `AdminLoadingLayout` montan su propio Frame sin él. Un componente del
 * encabezado que use la versión estricta tumba esas dos pantallas enteras —pantalla blanca— por
 * una lista de chats que ahí ni hace falta. Quien use esta versión debe manejar el `null`
 * mostrando menos, no rompiendo.
 */
export function useWaInboxNavOptional(): Ctx | null {
  return useContext(WaInboxNavContext);
}
